import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, NavLink, Route, Routes } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@fontsource-variable/inter';
import './app.css';
import { FolderKanban, MessagesSquare, FolderOpen, GanttChart, LayoutDashboard, ListChecks, LogOut, NotebookPen, Receipt, Users } from 'lucide-react';
import { FinancialsPage } from './pages/Financials.tsx';
import { Omnibar } from './components/Omnibar.tsx';
import { CaptureButton, InstallPrompt, MobileMenu, NotifyPrompt, useAppLaunch } from './components/Mobile.tsx';
import { loadMe, type Me } from './api.ts';
import { useDashboard } from './data.ts';
import { useLiveUpdates } from './live.ts';
import { TaskDrawer } from './components/TaskDrawer.tsx';
import { Avatar, ThemeToggle } from './components/ui.tsx';
import { CommandCenter } from './pages/CommandCenter.tsx';
import { ProjectPage, ProjectsPage } from './pages/Projects.tsx';
import { ChatPage } from './pages/Chat.tsx';
import { MyWorkPage, NotesPage, PeoplePage, TimelinePage, WorkspacePage } from './pages/Other.tsx';

const queryClient = new QueryClient({ defaultOptions: { queries: { staleTime: 10_000, retry: 1, refetchOnWindowFocus: false } } });

function Shell({ me }: { me: Me }) {
  useLiveUpdates();
  useAppLaunch();
  const d = useDashboard();
  const people = d.data?.people ?? [];
  return (
    <div className="shell">
      <nav className="side" aria-label="Main">
        <div className="brand"><img src="/favicon.svg" alt="" width={26} height={26} /><div><b>Great Basin</b><span>Operations</span></div></div>
        <NavLink to="/" end><LayoutDashboard size={16} aria-hidden /> Command center</NavLink>
        <NavLink to="/projects" end><FolderKanban size={16} aria-hidden /> Projects</NavLink>
        <div className="side-projects">{(d.data?.projects ?? []).map((p) => (
          <NavLink key={p.id} to={`/projects/${p.id}`} className="sub"><i style={{ background: p.color }} />{p.title}</NavLink>
        ))}</div>
        <NavLink to="/chat"><MessagesSquare size={16} aria-hidden /> Chat with Ops</NavLink>
        <NavLink to="/my-work"><ListChecks size={16} aria-hidden /> My work</NavLink>
        <NavLink to="/timeline"><GanttChart size={16} aria-hidden /> Timeline</NavLink>
        <NavLink to="/notes"><NotebookPen size={16} aria-hidden /> Notes log</NavLink>
        <NavLink to="/financials"><Receipt size={16} aria-hidden /> Financials</NavLink>
        <NavLink to="/workspace"><FolderOpen size={16} aria-hidden /> Workspace</NavLink>
        <NavLink to="/people"><Users size={16} aria-hidden /> People</NavLink>
        <div className="me">
          <Avatar email={me.email} people={people.length ? people : [{ email: me.email, name: me.name, picture: me.picture ?? '', role: '', active: true, last_login_at: null }]} size={28} />
          <div><span>{me.name || me.email}</span><form method="post" action="/logout"><button className="link sm"><LogOut size={11} aria-hidden /> Sign out</button></form></div>
        </div>
        <ThemeToggle />
      </nav>
      <div className="main">
        <div className="topbar"><MobileMenu projects={d.data?.projects ?? []} me={me} /><Omnibar aiEnabled={!!me.ai} model={me.model} /></div>
        <Routes>
          <Route path="/" element={<CommandCenter me={me} />} />
          <Route path="/projects" element={<ProjectsPage me={me} />} />
          <Route path="/projects/:id" element={<ProjectPage me={me} />} />
          <Route path="/chat" element={<ChatPage me={me} />} />
          <Route path="/chat/:id" element={<ChatPage me={me} />} />
          <Route path="/my-work" element={<MyWorkPage me={me} />} />
          <Route path="/timeline" element={<TimelinePage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/financials" element={<FinancialsPage />} />
          <Route path="/workspace" element={<WorkspacePage />} />
          <Route path="/people" element={<PeoplePage me={me} />} />
          <Route path="*" element={<div className="page">Not found.</div>} />
        </Routes>
      </div>
      {d.data && <TaskDrawer projects={d.data.projects} people={people} tasks={d.data.tasks} me={me} />}
      <CaptureButton />
      <InstallPrompt />
      <NotifyPrompt />
    </div>
  );
}

function App() {
  const [me, setMe] = useState<Me | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { loadMe().then(setMe).catch((e) => setErr(e.message)); }, []);
  if (err) return <p className="err pad">{err}</p>;
  if (!me) return <p className="muted pad">Loading…</p>;
  return <Shell me={me} />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode><QueryClientProvider client={queryClient}><BrowserRouter><App /></BrowserRouter></QueryClientProvider></StrictMode>,
);
